import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";
import { MatCardActions } from "@angular/material/card";
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from "@angular/material/dialog";
import { MatIcon } from "@angular/material/icon";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { SafeResourceUrl } from "@angular/platform-browser";
import { ConfirmDialogComponent, ConfirmDialogData } from "@cotecna/dialogs";
import {
  NgxExtendedPdfViewerComponent,
  NgxExtendedPdfViewerModule,
} from "ngx-extended-pdf-viewer";
import { Template } from "src/app/settings/models/template";
import { TemplatesService } from "src/app/settings/services/templates.service";

import { MultiSendByEmail } from "../../model/document-to-Share";
import { InspectionDocument } from "../../model/inspection-document";
import { InspectionDocumentService } from "../../services/inspection-document-service/inspection-document.service";
import { ShareEmailDialogComponent } from "../share-email-dialog/share-email-dialog.component";

@Component({
  selector: "im-view-documents-dialog",
  imports: [
    MatIcon,
    MatCardActions,
    MatProgressSpinnerModule,
    NgxExtendedPdfViewerModule,
  ],
  templateUrl: "./view-documents-dialog.component.html",
  styleUrl: "./view-documents-dialog.component.scss",
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class ViewDocumentsDialogComponent implements OnDestroy, OnInit {
  public documentName: string;
  public documentURL: SafeResourceUrl;
  public title: string;
  public minZoom: number = 50;
  public maxZoom: number = 300;
  public zoomLevel: number = 120;
  public isLoading: boolean = false;
  public isValidFile = true;
  public documents: InspectionDocument[];
  public currentIndex: number;
  public errorMessage: string;
  public objectUrl?: string;
  public selectedDocument: string;
  public templatesInfo: Template[] = [];
  private inspectionId: string;

  @ViewChild("pdfViewer", { static: false })
  public pdfViewer: NgxExtendedPdfViewerComponent;

  public constructor(
    private readonly dialogRef: MatDialogRef<ViewDocumentsDialogComponent>,
    private readonly documentService: InspectionDocumentService,
    private readonly templatesService: TemplatesService,
    private readonly dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA)
    private readonly dialogData: {
      inspectionId: string;
      selectedDocument: string;
      documents: InspectionDocument[];
    },
  ) {}

  public async ngOnInit() {
    this.selectedDocument = this.dialogData.selectedDocument;
    this.documents = this.dialogData.documents.filter((doc) => {
      const ext = doc.name.toLowerCase();
      return (
        ext.endsWith(".pdf") ||
        ext.endsWith(".doc") ||
        ext.endsWith(".docx") ||
        ext.endsWith(".xls") ||
        ext.endsWith(".xlsx")
      );
    });
    this.inspectionId = this.dialogData.inspectionId;
    this.setTitle();
    this.currentIndex = this.documents.findIndex(
      (doc) => doc.documentId === this.selectedDocument,
    );
    this.getTemplatesList();

    this.loadDocument();
  }

  public ngOnDestroy() {
    if (this.objectUrl) {
      URL.revokeObjectURL(this.objectUrl);
    }
  }

  public close(): void {
    this.dialogRef.close();
  }

  public onPdfLoaded() {
    if (this.pdfViewer) {
      this.pdfViewer.page.set(1);
    }
  }

  public handleDeleteKeyDown(event: KeyboardEvent) {
    if (event.key === "Enter" || event.key === " ") {
      this.delete();
      event.preventDefault();
    }
  }

  public async delete(): Promise<void> {
    if (!this.isValidFile) {
      return;
    }

    const confirmDialog = this.dialog.open(ConfirmDialogComponent, {
      data: <ConfirmDialogData>{
        icon: "delete_outline",
        okButton: {
          text: "delete",
          color: "warn",
        },
        title: "Delete Document",
        message: `Are you sure you want to delete "${this.documentName}" from the documents list?`,
      },
      panelClass: "share-document-dialog",
      autoFocus: false,
    });
    const confirmDialogResult = await confirmDialog.afterClosed().toPromise();
    if (confirmDialogResult) {
      await this.documentService.deleteDocument(
        this.inspectionId,
        this.documents[this.currentIndex],
      );
      this.dialogRef.close("refresh");
    }
  }

  public async download(): Promise<void> {
    if (!this.isValidFile) {
      return;
    }
    await this.documentService.downloadDocument(
      this.inspectionId,
      this.documents[this.currentIndex],
    );
  }

  public handleDownloadKeyDown(event: KeyboardEvent) {
    if (event.key === "Enter" || event.key === " ") {
      this.download();
      event.preventDefault();
    }
  }

  public previous(): void {
    this.errorMessage = "";
    this.isValidFile = true;
    if (this.currentIndex > 0) {
      this.currentIndex--;
      this.selectedDocument = this.documents[this.currentIndex].documentId;
      this.setTitle();
      this.loadDocument();
    }
  }

  public next(): void {
    this.errorMessage = "";
    this.isValidFile = true;
    if (this.currentIndex < this.documents.length - 1) {
      this.currentIndex++;
      this.selectedDocument = this.documents[this.currentIndex].documentId;
      this.setTitle();
      this.loadDocument();
    }
  }

  public zoomIn(): void {
    if (this.zoomLevel < this.maxZoom) {
      this.zoomLevel = Math.min(this.zoomLevel + 10, this.maxZoom);
    }
  }

  public zoomOut(): void {
    if (this.zoomLevel > this.minZoom) {
      this.zoomLevel = Math.max(this.zoomLevel - 10, this.minZoom);
    }
  }

  public handleShareByEmailKeyDown(event: KeyboardEvent) {
    if (event.key === "Enter" || event.key === " ") {
      this.shareByEmail();
      event.preventDefault();
    }
  }

  public async shareByEmail(): Promise<void> {
    if (!this.isValidFile) {
      return;
    }
    const document = this.documents[this.currentIndex];
    let defaultEmails = [];
    let allowAdditionalEmails = true;
    const foundTemplate = this.templatesInfo.find(
      (template) => template.templateId == document.templateId,
    );
    if (foundTemplate) {
      defaultEmails = foundTemplate.defaultEmails;
      allowAdditionalEmails = foundTemplate.allowAdditionalEmails;
    }
    const dialogRef = this.dialog.open(ShareEmailDialogComponent, {
      data: {
        defaultEmails: defaultEmails,
        allowAdditionalEmails: allowAdditionalEmails,
      },
      panelClass: "dialog",
      width: "40vw",
      height: "40vh",
      autoFocus: false,
    });
    const result = await dialogRef?.afterClosed().toPromise();
    if (result) {
      const multiSendByEmail: MultiSendByEmail = {
        inspectionId: this.inspectionId,
        documentsToSend: [
          {
            documentId: document.documentId,
            documentName: document.name,
          },
        ],
        destinationEmails: result.emailsToSend,
      };
      await this.documentService.sendByEmail(multiSendByEmail);
    }
  }

  private setTitle(index?: number): void {
    let docIndex = index;
    docIndex ??= this.documents.findIndex(
      (doc) => doc.documentId === this.selectedDocument,
    );

    if (docIndex === -1) {
      docIndex = this.currentIndex;
    }

    this.documentName = this.documents[docIndex].name;
    this.title = `(${docIndex + 1}/${this.documents.length}) ${this.documentName}`;
  }

  private async getTemplatesList(): Promise<Template[]> {
    this.templatesInfo = await this.templatesService.getTemplatesWithPreset(
      this.inspectionId,
    );
    if (this.templatesInfo.length == 0)
      this.templatesInfo = await this.templatesService.getTemplatesList();
    return this.templatesInfo;
  }

  private async loadDocument() {
    try {
      this.isLoading = true;
      this.documentURL = null;
      const response = await this.documentService.previewDocument(
        this.inspectionId,
        this.selectedDocument,
      );

      if (response.type === "application/pdf") {
        if (this.objectUrl) {
          URL.revokeObjectURL(this.objectUrl);
        }
        this.objectUrl = URL.createObjectURL(response);
        this.documentURL = this.objectUrl;
        this.isValidFile = true;
      } else if (response.type === "text/plain") {
        const errorText = await response.text();
        this.errorMessage = `${errorText} `;
      }
    } catch {
      this.isValidFile = false;
    } finally {
      this.isLoading = false;
    }
  }
}
